import { REVEAL_SELECTOR, revealElements } from "./reveal";

/**
 * Heavily inspired by Heydon Pickering's Inclusive Components Pattern: https://inclusive-components.design/tabbed-interfaces/
 */
class Tabs {
  activeTab = null; // contains the href of the currently active tab
  constructor(tabsElement, contentWrapperElement) {
    this.tabsElement = tabsElement;

    // get all the possible tabs as DOM nodes
    this.tabs = Array.from(tabsElement.querySelectorAll('[role="tab"]'));

    // get all the possible panels
    this.panels = Array.from(contentWrapperElement.querySelectorAll('[role="tabpanel"]'));

    this.init();
  }

  init() {
    // first, determine which tab is initially active and set it as the active tab
    this.setActiveTab(this.findActiveTab());

    // add event listeners to each tab
    this.tabs.forEach((tab) => {
      // listen for the click event on each tab
      tab.addEventListener("click", (event) => {
        const clickedTab = event.target.closest('[role="tab"]');

        if (clickedTab) {
          event.preventDefault();
          this.setActiveTab(clickedTab.id, true);
        }
      });

      // also listen for the keydown event on each tab
      tab.addEventListener("keydown", (event) => {
        let index = this.tabs.indexOf(event.target);

        // discern the direction of the arrow keys and save it as the new index, unless its 'right'
        let direction =
          event.which === 38 // arrow up
            ? index - 1
            : event.which === 40 // arrow down
              ? index + 1
              : event.which === 39 // arrow right
                ? "right"
                : null;

        // if anything but the 'up' key was pressed
        if (direction !== null) {
          event.preventDefault();

          // if the right key was pressed, focus the associated panels content
          if (direction === "right") {
            const associatedPanel = this.getPanelForTab(tab.id);
            associatedPanel.focus();
            // if up or down was pressed, set the active tab to the next or previous tab. setActiveTab() will do everything else.
          } else {
            const newTab = this.tabs[direction];

            if (newTab) {
              this.setActiveTab(newTab.id, true);
            }
          }
        }
      });
    });

    window.addEventListener("resize", () => this.updateActiveMarker());
  }

  setActiveTab(tabId, focusTab = false) {
    const tabChanged = this.activeTab !== tabId;

    // unselect all tabs that are not the new active tab
    this.tabs.forEach((tab) => {
      tab.setAttribute("aria-selected", tab.id === tabId ? "true" : "false");
      tab.closest(".tab-menu__item")?.classList.toggle("tab-menu__item--active", tab.id === tabId);

      if (tab.id !== tabId) {
        tab.setAttribute("tabindex", "-1");
      } else {
        tab.removeAttribute("tabindex");
        if (focusTab) {
          tab.focus();
        }
      }
    });

    this.activeTab = tabId;
    this.updateActiveMarker(tabId);

    this.showTabPanel(tabId);

    if (tabChanged && focusTab) {
      this.animateActivePanel(tabId);
    }
  }

  updateActiveMarker(tabId = this.activeTab) {
    const activeTab = this.tabs.find((tab) => tab.id === tabId);
    const activeItem = activeTab?.closest(".tab-menu__item");

    if (!activeItem) {
      return;
    }

    this.tabsElement.style.setProperty("--tab-menu-active-x", `${activeItem.offsetLeft}px`);
    this.tabsElement.style.setProperty("--tab-menu-active-y", `${activeItem.offsetTop}px`);
    this.tabsElement.style.setProperty("--tab-menu-active-width", `${activeItem.offsetWidth}px`);
    this.tabsElement.style.setProperty("--tab-menu-active-height", `${activeItem.offsetHeight}px`);
  }

  showTabPanel(tabId) {
    const activePanel = this.getPanelForTab(tabId);

    // unselect all panels that are not the new active panel
    this.panels.forEach((panel) => {
      if (panel !== activePanel) {
        panel.hidden = true;
        panel.setAttribute("tabindex", "-1");
      } else {
        panel.hidden = false;
        panel.removeAttribute("tabindex");
      }
    });
  }

  animateActivePanel(tabId) {
    const panel = this.getPanelForTab(tabId);

    if (!panel) {
      return;
    }

    const revealItems = Array.from(panel.querySelectorAll(REVEAL_SELECTOR));
    const listItems = Array.from(panel.querySelectorAll(":scope > .flexible-list > li"));
    const siblingItems = Array.from(panel.querySelectorAll(":scope > :not(.flexible-list)"));
    const items =
      revealItems.length || listItems.length || siblingItems.length
        ? [...revealItems, ...listItems, ...siblingItems]
        : Array.from(panel.children);
    const uniqueItems =
      revealItems.length || listItems.length || siblingItems.length
        ? Array.from(new Set(items))
        : items;

    if (!uniqueItems.length) {
      return;
    }

    revealElements(uniqueItems);
  }

  getPanelForTab(tabId, returnDOM = true) {
    const tab = this.tabs.find((tab) => tab.id === tabId);
    const panelId = tab?.getAttribute("aria-controls") || tabId.replace(/^tab-/, "tab-content-");
    const panel = this.panels.find((panel) => panel.id === panelId);

    return returnDOM ? panel : panel?.id;
  }

  // finds out which of the tabs is the currently active tab and returns its id
  findActiveTab() {
    let matchedTabId = null;

    // first, see if the current URL contains a fragment link that can point to any of the tabs.
    this.tabs.forEach((tab) => {
      if (window.location.href.includes(tab.getAttribute("href"))) {
        matchedTabId = tab.id;
      }
    });

    // if that wasn't set, look for the aria-selected attribute to determine that it is active.
    if (!matchedTabId) {
      this.tabs.forEach((tab) => {
        if (tab.getAttribute("aria-selected") === "true") {
          matchedTabId = tab.id;
        }
      });
    }

    // if nothing was set, assert that the first tab needs to be active.
    matchedTabId = !matchedTabId ? this.tabs[0].id : matchedTabId;

    return matchedTabId;
  }

  getActiveTab() {
    return this.activeTab;
  }
}

const tabsMenu = document.querySelector("[data-tabs-menu]");
const tabsContentWrapper = document.querySelector("[data-tabs-content-wrapper]");

if (tabsMenu && tabsContentWrapper) {
  new Tabs(tabsMenu, tabsContentWrapper);
}
